import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  MessageCircle, Save, Send, AlertCircle, CheckCircle, X, Lock, Loader2,
  Info, Eye, EyeOff, Power, Smartphone, Sparkles
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_META = {
  interakt: {
    label: 'Interakt',
    docsUrl: 'https://www.interakt.shop/resource-center/integrations/',
    defaultBaseUrl: 'https://api.interakt.ai/v1/public',
    secretLabel: 'Interakt API Key',
    secretPlaceholder: 'Paste API Key from Interakt → Developer Settings',
  },
  wati: {
    label: 'Wati',
    docsUrl: 'https://docs.wati.io/reference/introduction',
    defaultBaseUrl: 'https://live-mt-server.wati.io',
    secretLabel: 'Wati Access Token',
    secretPlaceholder: 'Paste Access Token from Wati → API Endpoint',
  },
};

const WhatsAppConfiguration = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Visibility toggles for secret inputs (only when user types a new one)
  const [showInteraktSecret, setShowInteraktSecret] = useState(false);
  const [showWatiSecret, setShowWatiSecret] = useState(false);

  const [form, setForm] = useState({
    provider: 'interakt',
    is_active: false,
    default_country_code: '+91',
    interakt_api_key: '',
    interakt_base_url: 'https://api.interakt.ai/v1/public',
    wati_access_token: '',
    wati_tenant_id: '',
    wati_base_url: 'https://live-mt-server.wati.io',
    wati_channel_number: '',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp-config`);
      if (data) {
        setConfig(data);
        setForm({
          provider: data.provider || 'interakt',
          is_active: !!data.is_active,
          default_country_code: data.default_country_code || '+91',
          // Pre-fill the secret inputs with the masked display string. The
          // backend treats any value that contains "•" as "keep existing".
          interakt_api_key: data.interakt_api_key_masked || '',
          interakt_base_url: data.interakt_base_url || PROVIDER_META.interakt.defaultBaseUrl,
          wati_access_token: data.wati_access_token_masked || '',
          wati_tenant_id: data.wati_tenant_id || '',
          wati_base_url: data.wati_base_url || PROVIDER_META.wati.defaultBaseUrl,
          wati_channel_number: data.wati_channel_number || '',
        });
      } else {
        setConfig(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load WhatsApp configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (provider) => {
    setForm((f) => ({
      ...f,
      provider,
      interakt_base_url: f.interakt_base_url || PROVIDER_META.interakt.defaultBaseUrl,
      wati_base_url: f.wati_base_url || PROVIDER_META.wati.defaultBaseUrl,
    }));
  };

  // Detect that user has typed a new secret (no bullets) so we send it as plaintext.
  const isMasked = (val) => typeof val === 'string' && val.indexOf('\u2022') >= 0;

  const handleSave = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = {
        provider: form.provider,
        is_active: form.is_active,
        default_country_code: form.default_country_code || '+91',
        interakt_api_key: isMasked(form.interakt_api_key) ? null : (form.interakt_api_key || null),
        interakt_base_url: form.interakt_base_url || PROVIDER_META.interakt.defaultBaseUrl,
        wati_access_token: isMasked(form.wati_access_token) ? null : (form.wati_access_token || null),
        wati_tenant_id: form.wati_tenant_id || null,
        wati_base_url: form.wati_base_url || PROVIDER_META.wati.defaultBaseUrl,
        wati_channel_number: form.wati_channel_number || null,
      };
      const { data } = await axios.put(`${API_URL}/whatsapp-config`, payload);
      setConfig(data);
      setForm((f) => ({
        ...f,
        interakt_api_key: data.interakt_api_key_masked || '',
        wati_access_token: data.wati_access_token_masked || '',
      }));
      setShowInteraktSecret(false);
      setShowWatiSecret(false);
      setSuccess('WhatsApp configuration saved. Click "Test Connection" to verify credentials.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save WhatsApp configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError('');
    setSuccess('');
    setTesting(true);
    try {
      const { data } = await axios.post(`${API_URL}/whatsapp-config/test-connection`);
      if (data.ok) {
        setSuccess(`Connection successful! Found ${data.template_count} approved template(s) on ${PROVIDER_META[form.provider].label}.`);
        await fetchConfig();
      } else {
        setError(data.error || 'Connection failed');
        await fetchConfig();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  const meta = PROVIDER_META[form.provider];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center" data-testid="wa-config-title">
          <MessageCircle className="mr-3" size={32} />
          WhatsApp Configuration
        </h1>
        <p className="text-gray-600 mt-1">
          Connect your AMC&apos;s WhatsApp Business account via Interakt or Wati. This stores
          your provider credentials; sending is wired up in a later phase.
        </p>
      </div>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start" data-testid="wa-config-success">
          <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1 text-sm text-green-800">{success}</div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-700" data-testid="wa-config-success-dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start" data-testid="wa-config-error">
          <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1 text-sm text-red-800 whitespace-pre-wrap">{error}</div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-700" data-testid="wa-config-error-dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      {config && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg" data-testid="wa-config-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`h-3 w-3 rounded-full mr-3 ${config.last_test_ok ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {config.last_test_ok ? 'Connection verified' : 'Saved but not yet verified'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {PROVIDER_META[config.provider]?.label || config.provider}
                  {config.is_active ? ' · Active' : ' · Inactive'}
                  {config.last_test_at && ` · last tested ${new Date(config.last_test_at).toLocaleString()}`}
                </p>
                {config.last_test_error && !config.last_test_ok && (
                  <p className="text-xs text-red-600 mt-1 max-w-2xl">⚠ {config.last_test_error}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        {/* Provider selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Provider</label>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(PROVIDER_META).map(([key, p]) => (
              <button
                type="button"
                key={key}
                onClick={() => handleProviderChange(key)}
                className={`px-4 py-3 border rounded-lg text-sm font-medium transition-colors ${
                  form.provider === key
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`wa-config-provider-${key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active toggle + country code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Country Code *</label>
            <input
              type="text"
              required
              value={form.default_country_code}
              onChange={(e) => setForm({ ...form, default_country_code: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="+91"
              data-testid="wa-config-country-code"
            />
            <p className="text-xs text-gray-500 mt-1">Prepended to recipient mobile numbers when missing.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Power size={14} className={`mr-1.5 ${form.is_active ? 'text-green-600' : 'text-amber-500'}`} />
              Active *
            </label>
            <label className={`inline-flex items-center cursor-pointer h-10 px-3 -mx-3 rounded-md transition-colors ${
              form.is_active ? '' : 'bg-amber-50 border border-amber-200'
            }`}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                data-testid="wa-config-active-toggle"
              />
              <span className={`ml-2 text-sm ${form.is_active ? 'text-gray-700' : 'text-amber-900 font-medium'}`}>
                {form.is_active
                  ? 'WhatsApp is active for this AMC'
                  : 'WhatsApp is inactive — tick to enable sending'}
              </span>
            </label>
            {!form.is_active && (
              <p className="text-xs text-amber-700 mt-1">
                Required before any WhatsApp sends (including test messages) will run.
              </p>
            )}
          </div>
        </div>

        {/* Provider-specific fields */}
        {form.provider === 'interakt' && (
          <div className="space-y-4 border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-900">Interakt Credentials</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {meta.secretLabel} {!config?.has_interakt_api_key && '*'}
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showInteraktSecret ? 'text' : 'password'}
                  value={form.interakt_api_key}
                  onChange={(e) => setForm({ ...form, interakt_api_key: e.target.value })}
                  onFocus={(e) => {
                    if (isMasked(e.target.value)) {
                      setForm((f) => ({ ...f, interakt_api_key: '' }));
                    }
                  }}
                  className="w-full pl-9 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={meta.secretPlaceholder}
                  required={!config?.has_interakt_api_key}
                  data-testid="wa-config-interakt-key"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowInteraktSecret((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="wa-config-interakt-key-toggle"
                >
                  {showInteraktSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Stored encrypted (Fernet). Leave the masked value unchanged to keep the existing key.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Interakt Base URL</label>
              <input
                type="url"
                value={form.interakt_base_url}
                onChange={(e) => setForm({ ...form, interakt_base_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder={PROVIDER_META.interakt.defaultBaseUrl}
                data-testid="wa-config-interakt-base-url"
              />
            </div>
          </div>
        )}

        {form.provider === 'wati' && (
          <div className="space-y-4 border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-900">Wati Credentials</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {meta.secretLabel} {!config?.has_wati_access_token && '*'}
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showWatiSecret ? 'text' : 'password'}
                  value={form.wati_access_token}
                  onChange={(e) => setForm({ ...form, wati_access_token: e.target.value })}
                  onFocus={(e) => {
                    if (isMasked(e.target.value)) {
                      setForm((f) => ({ ...f, wati_access_token: '' }));
                    }
                  }}
                  className="w-full pl-9 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={meta.secretPlaceholder}
                  required={!config?.has_wati_access_token}
                  data-testid="wa-config-wati-token"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowWatiSecret((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="wa-config-wati-token-toggle"
                >
                  {showWatiSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Stored encrypted (Fernet). Leave masked to keep existing.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Wati Tenant ID *</label>
                <input
                  type="text"
                  required
                  value={form.wati_tenant_id}
                  onChange={(e) => setForm({ ...form, wati_tenant_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="e.g. 38xxxx"
                  data-testid="wa-config-wati-tenant"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Wati Channel Number</label>
                <input
                  type="text"
                  value={form.wati_channel_number}
                  onChange={(e) => setForm({ ...form, wati_channel_number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. +14155551234"
                  data-testid="wa-config-wati-channel"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Wati Base URL</label>
              <input
                type="url"
                value={form.wati_base_url}
                onChange={(e) => setForm({ ...form, wati_base_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder={PROVIDER_META.wati.defaultBaseUrl}
                data-testid="wa-config-wati-base-url"
              />
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
          <Info size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            <strong>Note:</strong> WhatsApp templates must be pre-approved by Meta on the vendor side
            (Interakt / Wati dashboard). Prime AIF will pull approved templates and let you map their
            variables — see <span className="font-mono">WhatsApp Templates</span> in the sidebar after saving.
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={handleTest}
            disabled={!config || testing}
            className="px-5 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="wa-config-test-connection"
          >
            {testing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
            Test Connection
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center disabled:opacity-50"
            data-testid="wa-config-save-btn"
          >
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
            {config ? 'Update Configuration' : 'Save Configuration'}
          </button>
        </div>
      </form>

      <TestSendPanel apiUrl={API_URL} config={config} onConfigChanged={fetchConfig} />
    </div>
  );
};

// ----------------------------------------------------------------------
// "Send to me" — Phase 2A.1 live test affordance.
// Calls the same Phase 2A engine but bypasses any drawdown context.
// ----------------------------------------------------------------------
const TestSendPanel = ({ apiUrl, config, onConfigChanged }) => {
  const [preflight, setPreflight] = useState({ ok: false, reason: 'Checking…', templates: [] });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activating, setActivating] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [countryCode, setCountryCode] = useState(config?.default_country_code || '+91');
  const [phone, setPhone] = useState('');
  const [result, setResult] = useState(null); // {ok, message_id, body_values} | null
  const [error, setError] = useState('');

  // True when the ONLY thing blocking sends is the Active toggle (config saved,
  // last test passed, just inactive). Lets us offer a one-click activate fix.
  const blockedOnInactiveOnly =
    config && config.has_interakt_api_key && config.last_test_ok && !config.is_active;

  useEffect(() => {
    refreshPreflight();
    // eslint-disable-next-line
  }, [config?.id, config?.last_test_ok, config?.last_synced_at]);

  useEffect(() => {
    if (config?.default_country_code) {
      setCountryCode((c) => c || config.default_country_code);
    }
  }, [config?.default_country_code]);

  const refreshPreflight = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/whatsapp/test-send/preflight`);
      setPreflight(data || { ok: false, reason: 'Unknown', templates: [] });
      if (data?.ok && (data.templates || []).length > 0) {
        // Default to the first non-doc-header template (those can't be tested in isolation).
        const firstTestable = (data.templates || []).find((t) => t.header_type !== 'document');
        const fallback = (data.templates || [])[0];
        setTemplateId((current) => current || (firstTestable?.id || fallback?.id || ''));
      } else {
        setTemplateId('');
      }
    } catch (err) {
      setPreflight({ ok: false, reason: err.response?.data?.detail || 'Failed to check readiness', templates: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(`${apiUrl}/whatsapp/test-send`, {
        template_id: templateId,
        country_code: countryCode || '+91',
        phone_number: (phone || '').trim(),
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  // One-click fix for the most common blocker — the admin saved a config + tested
  // it but forgot to tick the Active checkbox. PUT the same config back with the
  // single field flipped, then refresh preflight + bubble the change up.
  const handleActivate = async () => {
    if (!config) return;
    setActivating(true);
    setError('');
    try {
      await axios.put(`${apiUrl}/whatsapp-config`, {
        provider: config.provider,
        is_active: true,
        default_country_code: config.default_country_code || '+91',
        // Secrets unchanged — the masked echo tells the backend to keep existing.
        interakt_api_key: config.interakt_api_key_masked || null,
        interakt_base_url: config.interakt_base_url || null,
        wati_access_token: config.wati_access_token_masked || null,
        wati_tenant_id: config.wati_tenant_id || null,
        wati_base_url: config.wati_base_url || null,
        wati_channel_number: config.wati_channel_number || null,
      });
      await onConfigChanged?.();
      await refreshPreflight();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate WhatsApp');
    } finally {
      setActivating(false);
    }
  };

  const disabled = loading || sending || !preflight.ok || !templateId || !(phone || '').trim();

  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6" data-testid="wa-test-send-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            <Sparkles size={16} className="text-blue-600 mr-2" />
            Send test message
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Pick a published drawdown-category template, enter your own WhatsApp number, and
            send a real message via Interakt. Body values come from preview sample data — no
            investor data is touched.
          </p>
        </div>
        <button
          type="button"
          onClick={refreshPreflight}
          className="text-xs text-blue-600 hover:underline"
          data-testid="wa-test-send-refresh"
        >
          Refresh
        </button>
      </div>

      {!preflight.ok && (
        <div
          className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 flex items-start gap-2"
          data-testid="wa-test-send-blocked"
          title={preflight.reason || ''}
        >
          <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{preflight.reason || 'Not ready.'}</p>
            {blockedOnInactiveOnly && (
              <p className="mt-1 text-amber-800">
                Your config is saved and your connection test passed — just tick <strong>Active</strong>{' '}
                in the form above and save, or click below.
              </p>
            )}
          </div>
          {blockedOnInactiveOnly && (
            <button
              type="button"
              onClick={handleActivate}
              disabled={activating}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center disabled:opacity-50"
              data-testid="wa-test-send-activate-now"
            >
              {activating ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Power size={12} className="mr-1.5" />}
              Activate now
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Template *</label>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={!preflight.ok || sending}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
            data-testid="wa-test-send-template"
          >
            {!preflight.templates?.length && <option value="">(no templates available)</option>}
            {(preflight.templates || []).map((t) => (
              <option key={t.id} value={t.id} disabled={t.header_type === 'document'}>
                {t.name} ({t.language_code}){t.header_type === 'document' ? ' · doc-header (not testable)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Country code</label>
          <input
            type="text"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={!preflight.ok || sending}
            placeholder="+91"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed font-mono"
            data-testid="wa-test-send-country-code"
          />
        </div>
        <div className="md:col-span-5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Your WhatsApp number *</label>
          <div className="relative">
            <Smartphone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={!preflight.ok || sending}
              placeholder="9876543210"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed font-mono"
              data-testid="wa-test-send-number"
            />
          </div>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 mt-2">
        <Info size={10} className="inline -mt-0.5 mr-1" />
        The number must be opted-in to receive marketing templates from your WhatsApp Business
        account (some approved templates require the user to have messaged the business first).
      </p>

      <div className="flex items-center justify-end gap-3 mt-4">
        <button
          type="button"
          onClick={handleSend}
          disabled={disabled}
          title={!preflight.ok ? preflight.reason : (!templateId ? 'Pick a template' : (!(phone || '').trim() ? 'Enter a number' : 'Send a real WhatsApp message'))}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="wa-config-test-send"
        >
          {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
          Send to this number
        </button>
      </div>

      {result && (
        <div
          className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start text-sm"
          data-testid="wa-test-send-result-success"
        >
          <CheckCircle size={16} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-green-900">Sent to {result.country_code}{result.phone_number}.</p>
            <p className="text-xs text-green-800 mt-0.5">
              Interakt message id:{' '}
              <span className="font-mono">{result.interakt_message_id || '(not returned)'}</span>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Sample body values used:{' '}
              <span className="font-mono">{(result.resolved_body_values || []).join(' · ') || '(none)'}</span>
            </p>
          </div>
          <button
            onClick={() => setResult(null)}
            className="text-green-600 hover:text-green-700"
            data-testid="wa-test-send-result-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {error && (
        <div
          className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start text-sm"
          data-testid="wa-test-send-result-error"
        >
          <AlertCircle size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-900">Send failed.</p>
            <p className="text-xs text-red-800 whitespace-pre-wrap mt-0.5">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-700">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default WhatsAppConfiguration;
